import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { ClipLoader } from "react-spinners";
import styled, { css } from "styled-components";
import walletIcon from "@assets/images/wallet-icon.svg";
import { useServices } from "@core/servicesContext";
import { Group } from "@domain/group/group";
import { useWallet } from "@domain/wallet/hook";
import { useAsync } from "@utils/hooks";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { Selecter } from "@ui/components/inputs/selecter";
import { row, stack } from "@ui/components/layout";
import { ClosableModal } from "@ui/modals/closableModal";
import { useModalTools } from "@ui/modals/modalContext";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { OngoingTreatment } from "./ongoingTreatment";
interface ModalPaiementProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	shareroom: Group;
}

export const ModalPaiement: React.FC<ModalPaiementProps> = ({ close, shareroom, ...htmlProps }) => {
	const [serviceCode, setServiceCode] = useState<string>("");
	const [phoneNumber, setPhoneNumber] = useState("");
	const { result: wallets } = useWallet();
	const { walletService } = useServices();
	const { createModal } = useModalTools();
	const { run, loading } = useAsync(async () => {
		try {
			const apiwallet = await walletService.save(
				{ id: "", shipperNumber: phoneNumber, serviceCode: serviceCode },
				shareroom.id
			);
			close();
			if (apiwallet.paymentUrl) {
				global.window.location.href = apiwallet.paymentUrl;
			} else {
				createModal((close) => <OngoingTreatment close={close} shareroom={shareroom} />);
			}
		} catch (e) {
			console.error(e);
			createToast(I18n.t("error"), I18n.t("auth.error.default"), "error");
		}
	}, [phoneNumber, serviceCode]);
	return (
		<Container close={close} {...htmlProps}>
			<ContainerLabel>
				<img src={walletIcon} />
				<Info> {I18n.t("shareroom.subscription.title")} </Info>
			</ContainerLabel>
			<InfoEdition>
				<PrimaryMedium>{I18n.t("shareroom.subscription.mobile_money.title")} </PrimaryMedium>
				{wallets && (
					<Selecter
						value={serviceCode}
						onChange={(value) => setServiceCode(value)}
						options={wallets?.map((val) => ({
							value: val.value,
							label: val.label,
						}))}
						placeholder={I18n.t("shareroom.subscription.mobile_money.title")}
					/>
				)}
			</InfoEdition>

			<InfoEdition>
				<PrimaryMedium>{I18n.t("shareroom.subscription.phone.title")}</PrimaryMedium>
				<TextInput
					value={phoneNumber}
					onChange={(e) => {
						const value = e.currentTarget.value;
						setPhoneNumber(value);
					}}
					placeholder="00 000 00 00"
				/>
			</InfoEdition>
			{loading ? (
				<ClipLoader size={60} />
			) : (
				<BlocButton>
					<StyledButtonContainer onClick={() => run()} disabled={phoneNumber.length < 9 || serviceCode.length <= 0}>
						{I18n.t("confirm")}
					</StyledButtonContainer>
				</BlocButton>
			)}
		</Container>
	);
};

const Container = styled(ClosableModal)`
	${gap(20)};
	display: flex;
	justify-content: center;
	align-items: center;
`;
const ContainerLabel = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
`;

const Info = styled.div`
	${row(16, "flex-start", "stretch")};
	${({ theme }) => theme.textStyles.description};
	font-weight: 400;
	font-size: 15px;
	line-height: 30px;
	text-align: justify;
	color: #000000;
	margin-top: 30px;
	margin-bottom: 18px;
`;

const InfoEdition = styled.div`
	gap: 12px;
	${stack(10, "flex-start", "stretch")};
	width: 80%;
`;

const TextInput = styled.input<{ isInvalid?: boolean }>`
	${({ theme }) => theme.containerStyles.roundedBlockStyle};
	padding: 10px 12px 9px;
	border-radius: 10px;
	font-size: 0.935rem;
	outline: none;

	:disabled {
		color: ${({ theme }) => theme.colors.textTertiary};
	}

	:focus {
		border: 1px solid ${({ theme, isInvalid }) => (isInvalid ? theme.colors.red : theme.colors.secondary)};
	}

	${({ isInvalid }) =>
		isInvalid &&
		css`
			border: 1px solid ${({ theme }) => theme.colors.red};
		`}

	@media (max-width: 370px) {
		width: 140px;
	}
`;

const BlocButton = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	margin-top: 30px;
`;
const StyledButtonContainer = styled(PrimaryButton)``;
